// The Copilot's action cards (plans, proposals, agent decisions) and their
// receipts: one look in the side panel and on the full Copilot page. Built
// from src/design tokens; the tone text colours are the SENTIMENT ramp
// darkened enough to read at 11 px on its own tint.
import styled, { css, keyframes } from "styled-components";
import { FONT, FIG_INK, FIG_MUTED, CARD_BORDER, HAIRLINE, PRIMARY, PRIMARY_SOFT, SENTIMENT } from "../../design";

export const TONE = {
  amber: { fg: "#9A4A06", bg: "#FDF0DE" },
  green: { fg: "#22665A", bg: "#E3F1EC" },
  red: { fg: "#9B3F33", bg: "#F8E6E3" },
  blue: { fg: PRIMARY, bg: "#EEF0FC" },
  grey: { fg: "#5E5B58", bg: "#F1F0EF" },
};

export const Card = styled.div`
  font-family: ${FONT};
  border: 1px solid ${CARD_BORDER};
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
  margin: 8px 0 4px;
  box-shadow: 0 2px 10px rgba(44, 41, 38, 0.05);
`;

export const CardTop = styled.div`
  padding: 12px 14px;
  .row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .meta { font-size: 11px; color: ${FIG_MUTED}; white-space: nowrap; }
  .who { font-size: 11.5px; color: ${FIG_MUTED}; margin: 8px 0 0; }
`;

export const Kicker = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 3px 8px;
  color: ${(p) => (TONE[p.$tone] || TONE.grey).fg};
  background: ${(p) => (TONE[p.$tone] || TONE.grey).bg};
`;

export const CardTitle = styled.div`
  font-size: ${(p) => (p.$big ? 15 : 14)}px;
  font-weight: 600;
  color: ${FIG_INK};
  line-height: 1.35;
  margin-top: 8px;
`;

export const CardText = styled.p`
  font-size: 12.5px;
  color: #4e4b48;
  line-height: 1.5;
  margin: 6px 0 0;
`;

export const Section = styled.div`
  padding: 10px 14px;
  border-top: 1px solid ${HAIRLINE};
`;

export const SectionLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${FIG_MUTED};
  margin: 0 0 8px;
`;

export const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const StepItem = styled.li`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 12.5px;
  line-height: 1.45;
  .ico { flex: none; width: 20px; height: 20px; display: inline-flex; align-items: center; justify-content: center; }
  .num {
    flex: none; width: 20px; height: 20px; border-radius: 50%; border: 1px solid #c9d1f7; color: ${PRIMARY};
    font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center;
  }
  b { font-weight: 600; color: ${FIG_INK}; display: block; }
  .r { color: #4e4b48; display: block; }
  .x { color: #7c3328; display: block; }
  .w { color: ${FIG_MUTED}; display: block; }
`;

export const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 500;
  border-radius: 999px;
  padding: 3px 9px;
  color: ${(p) => (TONE[p.$tone] || TONE.grey).fg};
  background: ${(p) => (TONE[p.$tone] || TONE.grey).bg};
`;

export const CardFoot = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  padding: 12px 14px;
  border-top: 1px solid ${HAIRLINE};
  background: #fafafb;
  .note { font-size: 12px; color: ${FIG_MUTED}; line-height: 1.45; flex: 1 1 180px; }
`;

const buttonBase = css`
  font-family: ${FONT};
  border-radius: 10px;
  padding: 9px 14px;
  font-size: 13px;
  min-height: 38px;
  cursor: pointer;
  white-space: nowrap;
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 2px; }
  &:disabled { opacity: 0.55; cursor: default; }
`;

export const Primary = styled.button`
  ${buttonBase}
  border: 0;
  background: ${PRIMARY};
  color: #fff;
  font-weight: 600;
  &:hover:not(:disabled) { background: #243ab8; }
`;

export const Secondary = styled.button`
  ${buttonBase}
  border: 1px solid #dcdad8;
  background: #fff;
  color: ${FIG_INK};
  font-weight: 500;
  &:hover:not(:disabled) { border-color: ${PRIMARY}; color: ${PRIMARY}; }
`;

export const TextButton = styled.button`
  ${buttonBase}
  border: 0;
  background: none;
  color: ${(p) => (p.$muted ? FIG_MUTED : PRIMARY)};
  font-weight: 600;
  padding: 6px 4px;
  min-height: 0;
  font-size: 12.5px;
  ${(p) => p.$push && css`margin-left: auto;`}
  &:hover:not(:disabled) { color: ${(p) => (p.$muted ? FIG_INK : "#243ab8")}; }
`;

// A finished action in one line: what happened, when, and Undo / Details.
export const Receipt = styled.div`
  font-family: ${FONT};
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid ${(p) => (p.$tone === "green" ? "#d5e9e2" : p.$tone === "red" ? "#efd3ce" : HAIRLINE)};
  background: ${(p) => (p.$tone === "green" ? "#f4faf7" : p.$tone === "red" ? "#fcf5f4" : "#fafafb")};
  border-radius: 12px;
  padding: 10px 12px;
  margin: 8px 0 4px;
  .t { flex: 1; min-width: 0; }
  b { display: block; font-size: 13px; font-weight: 600; color: ${FIG_INK}; line-height: 1.35; }
  .t span { display: block; font-size: 11.5px; color: #4e4b48; margin-top: 2px; }
  .acts { display: flex; gap: 6px; align-items: center; }
`;

export const SmallButton = styled.button`
  font-family: ${FONT};
  background: #fff;
  color: ${FIG_INK};
  border: 1px solid #dcdad8;
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  min-height: 32px;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) { border-color: ${PRIMARY}; color: ${PRIMARY}; }
  &:focus-visible { outline: 2px solid ${PRIMARY}; outline-offset: 2px; }
  &:disabled { opacity: 0.55; cursor: default; }
`;

// Key numbers (a proposal's goal, a decision's impact).
export const Numbers = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols || 2}, minmax(0, 1fr));
  gap: 8px;
  > div { background: #f7f7f9; border-radius: 10px; padding: 8px 10px; }
  b { display: block; font-size: 15px; font-weight: 700; color: ${FIG_INK}; }
  span { font-size: 11px; color: ${FIG_MUTED}; line-height: 1.3; display: block; }
`;

export const Change = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  .from { font-size: 17px; font-weight: 600; color: ${FIG_MUTED}; text-decoration: line-through; }
  .to { font-size: 21px; font-weight: 700; color: ${FIG_INK}; }
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
export const Spinner = styled.span`
  display: inline-block;
  width: ${(p) => p.$size || 16}px;
  height: ${(p) => p.$size || 16}px;
  border-radius: 50%;
  border: 2px solid #eef0f8;
  border-top-color: ${PRIMARY};
  animation: ${spin} 0.9s linear infinite;
  flex: none;
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

export const Bar = styled.div`
  height: ${(p) => p.$h || 4}px;
  border-radius: 999px;
  background: #eef0f8;
  overflow: hidden;
  i { display: block; height: 100%; background: ${PRIMARY}; border-radius: 999px; transition: width 0.4s ease; }
`;

export const SoftNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #4e4b48;
`;

export const Highlight = styled.span`
  color: ${PRIMARY};
  background: ${PRIMARY_SOFT};
`;

export const POSITIVE = SENTIMENT.POSITIVE;
